import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useQueries } from '@tanstack/react-query';
import { Input, Select, Space, Table, Typography } from 'antd';
import { Alert, Button, Status } from '@insight-health-ai/roseate/components';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { magicFlowApi } from 'src/services';
import { useMagicFlowContext } from '../MagicFlowContext';
import type {
  FlowEvent,
  FlowRunPriority,
  FlowRun,
  FlowRunStatus,
} from 'src/models';

type FlowRunTableRecord = FlowRun & { key: string };

type StatusAppearance = 'success' | 'error' | 'warning' | 'info' | 'neutral';

const statusAppearance = (label?: string): StatusAppearance => {
  if (!label) return 'neutral';
  const v = label.toLowerCase();
  if (v.includes('complete') || v.includes('success')) return 'success';
  if (v.includes('fail') || v.includes('error') || v.includes('cancel'))
    return 'error';
  if (v.includes('progress') || v.includes('running') || v.includes('active'))
    return 'info';
  if (v.includes('pending') || v.includes('wait') || v.includes('queue'))
    return 'warning';
  return 'neutral';
};

const renderStatus = (status: FlowRunStatus | string | null | undefined) => {
  if (!status) return <Status appearance="neutral" label="-" />;
  if (typeof status === 'string') {
    return <Status appearance={statusAppearance(status)} label={status} />;
  }
  const label = status.name || status.key || '-';
  return <Status appearance={statusAppearance(label)} label={label} />;
};

const renderPriority = (
  priority: FlowRunPriority | string | null | undefined
) => {
  if (!priority) return '-';
  if (typeof priority === 'string') return priority;
  return priority.name || priority.key || '-';
};

const shortId = (value?: string | null) => {
  if (!value) return '-';
  return value.length > 8 ? value.slice(0, 8) : value;
};

export const FlowRunsTab = () => {
  const navigate = useNavigate();
  const {
    hasOrgSelected,
    scopedHeaders,
    flowRunsTableData,
    flowRunsTotal,
    isLoadingFlowRuns,
    isFetchingFlowRuns,
    refreshFlowRuns,
    flowRunPage,
    setFlowRunPage,
    flowRunPageSize,
    setFlowRunPageSize,
    flowRunStatusIds,
    setFlowRunStatusIds,
    flowRunPriorityIds,
    setFlowRunPriorityIds,
    flowRunFlowIds,
    setFlowRunFlowIds,
    flowRunSearch,
    setFlowRunSearch,
    flowRunStatusOptions,
    flowRunPriorityOptions,
    flowRunFlowOptions,
    flowRunFlowsById,
    openFlowRunDetails,
  } = useMagicFlowContext();

  const [searchInput, setSearchInput] = useState(flowRunSearch);

  const uniqueEventIds = useMemo(() => {
    const ids = new Set<string>();
    flowRunsTableData.forEach((run) => {
      if (run.trigger_event_id) {
        ids.add(run.trigger_event_id);
      }
    });
    return Array.from(ids);
  }, [flowRunsTableData]);

  const eventQueries = useQueries({
    queries: uniqueEventIds.map((eventId) => ({
      queryKey: ['magicflow-event-detail', eventId],
      enabled: hasOrgSelected && Boolean(eventId),
      staleTime: 5 * 60 * 1000,
      queryFn: () =>
        magicFlowApi
          .get<{ event: FlowEvent }>(`/event/${eventId}`, {
            headers: scopedHeaders,
          })
          .then((res) => res.data.event),
    })),
  });

  const eventQueriesStateKey = eventQueries
    .map((q) => `${q.dataUpdatedAt}:${q.status}:${q.fetchStatus}`)
    .join('|');

  const eventNamesById = useMemo(() => {
    const map: Record<string, { name?: string; loading: boolean }> = {};
    uniqueEventIds.forEach((eventId, index) => {
      const query = eventQueries[index];
      map[eventId] = {
        name: query?.data?.event_name,
        loading: query?.isLoading || query?.isFetching || false,
      };
    });
    return map;
  }, [uniqueEventIds, eventQueriesStateKey]);

  const columns = useMemo<ColumnsType<FlowRunTableRecord>>(
    () => [
      {
        title: 'Flow run',
        dataIndex: 'flow_run_id',
        width: 240,
        render: (value, record) => {
          const flow = record.flow_id ? flowRunFlowsById[record.flow_id] : null;
          const flowName = flow?.name || 'unknown flow';
          return (
            <Space direction="vertical" size={0}>
              <Button
                variant="text"
                appearance="primary"
                size="sm"
                className="!p-0 text-left"
                onClick={() => openFlowRunDetails(record)}
              >
                {flowName}
                {flow?.version != null ? ` (v${flow.version})` : ''}
              </Button>
              <Typography.Text type="secondary" className="font-mono text-xs">
                {shortId(value)}
              </Typography.Text>
            </Space>
          );
        },
      },
      {
        title: 'Event',
        dataIndex: 'trigger_event_id',
        width: 220,
        render: (value: string | null | undefined) => {
          if (!value) {
            return (
              <Typography.Text type="secondary">manual / none</Typography.Text>
            );
          }
          const entry = eventNamesById[value];
          const name = entry?.name;
          return (
            <Space direction="vertical" size={0}>
              <Typography.Text>
                {name ||
                  (entry?.loading ? (
                    <Typography.Text type="secondary">loading…</Typography.Text>
                  ) : (
                    <Typography.Text type="secondary">unknown</Typography.Text>
                  ))}
              </Typography.Text>
              <Typography.Text type="secondary" className="font-mono text-xs">
                {shortId(value)}
              </Typography.Text>
            </Space>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 130,
        render: (_, record) => renderStatus(record.status),
      },
      {
        title: 'Priority',
        dataIndex: 'priority',
        width: 110,
        render: (_, record) => renderPriority(record.priority),
      },
      {
        title: 'Due date',
        dataIndex: 'due_date',
        width: 180,
        render: (value: string | undefined) =>
          value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-',
      },
      {
        title: 'Updated',
        dataIndex: 'updated_at',
        width: 180,
        render: (value: string | undefined) =>
          value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-',
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        width: 180,
        render: (value: string | undefined) =>
          value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        render: (_, record) => (
          <Space size={4}>
            <Button
              variant="outlined"
              appearance="secondary"
              size="sm"
              onClick={() => openFlowRunDetails(record)}
            >
              View
            </Button>
            {record.flow_id ? (
              <Button
                variant="outlined"
                appearance="secondary"
                size="sm"
                onClick={() =>
                  navigate(`/superadmin/magicflow/flows/${record.flow_id}`)
                }
              >
                Open flow
              </Button>
            ) : null}
          </Space>
        ),
      },
    ],
    [eventNamesById, flowRunFlowsById, navigate, openFlowRunDetails]
  );

  const handleSearchSubmit = () => {
    setFlowRunPage(1);
    setFlowRunSearch(searchInput.trim());
  };

  if (!hasOrgSelected) {
    return (
      <Alert
        appearance="info"
        message="Select an organization to view flow runs."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 p-4 space-y-3 bg-white">
        <Typography.Title level={5} className="!mb-0">
          Filters
        </Typography.Title>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Filter by flow"
            value={flowRunFlowIds}
            onChange={(value) => {
              setFlowRunPage(1);
              setFlowRunFlowIds(value as string[]);
            }}
            options={flowRunFlowOptions}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Filter by status"
            value={flowRunStatusIds}
            onChange={(value) => {
              setFlowRunPage(1);
              setFlowRunStatusIds(value as string[]);
            }}
            options={flowRunStatusOptions}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Filter by priority"
            value={flowRunPriorityIds}
            onChange={(value) => {
              setFlowRunPage(1);
              setFlowRunPriorityIds(value as string[]);
            }}
            options={flowRunPriorityOptions}
          />
          <Input
            allowClear
            placeholder="Search by run id or trigger event id"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onPressEnter={handleSearchSubmit}
            onBlur={handleSearchSubmit}
          />
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <Typography.Title level={5} className="!mb-0">
            Flow Runs ({flowRunsTotal})
          </Typography.Title>
          <Button
            variant="outlined"
            appearance="secondary"
            size="sm"
            icon={<ReloadOutlined />}
            onClick={refreshFlowRuns}
            disabled={isLoadingFlowRuns}
            loading={isFetchingFlowRuns}
          >
            Refresh
          </Button>
        </div>
        <div className="rounded-xl shadow-xl bg-white">
          <Table
            className="appointment-page-table"
            dataSource={flowRunsTableData}
            columns={columns}
            rowKey="key"
            loading={isLoadingFlowRuns || isFetchingFlowRuns}
            scroll={{ x: 1500 }}
            pagination={{
              current: flowRunPage,
              pageSize: flowRunPageSize,
              total: flowRunsTotal,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (newPage, newSize) => {
                setFlowRunPage(newPage);
                if (newSize && newSize !== flowRunPageSize) {
                  setFlowRunPageSize(newSize);
                }
              },
              showTotal: (total) => `${total} runs`,
            }}
            locale={{
              emptyText: 'No flow runs',
            }}
          />
        </div>
      </div>
    </div>
  );
};
